// Üst bar sorunlu kargo bildirimi — tarayıcı tarafı durum (SAF, bağımlılıksız; client bundle'a girer).
// "Yeni" = müdahale listesinde olup bu tarayıcıda henüz "görüldü" işaretlenmemiş sipariş. Listeden çıkan
// kimlik unutulur; sipariş yeniden sorunlu olursa tekrar yeni sayılır. İlk açılışta (kayıt yok) mevcut liste
// görülmüş kabul edilir — yüzlerce eski kayıt için bildirim yağmuru olmaz.
import type { CargoProblemAlertItem } from '@/lib/cargo-problem-alerts'

export interface NotifyState {
  /** Görüldü işaretlenen sipariş kimlikleri (yalnız listede kalanlar tutulur). */
  seen: string[]
  /** Sesli uyarısı verilmiş kimlikler — aynı sipariş için ikinci kez çalınmaz (sekmeler arası). */
  alerted: string[]
  /** Son çekilen liste: başka sekme yakın zamanda çektiyse yeniden istenmez, buradan okunur. */
  items: CargoProblemAlertItem[]
  fetchedAt: number
}

export const NOTIFY_POLL_MS = 60_000
/** Sekmeler arası: son çekimden bu kadar kısa süre geçtiyse istek atılmaz (tarayıcı başına ~dakikada bir). */
export const NOTIFY_SHARED_FETCH_GAP_MS = 50_000
export const NOTIFY_SOUND_KEY = 'opop:cargo-problem-alerts:sound'

export function notifyStorageKey(tenantId: string, userId: string): string {
  return `opop:cargo-problem-alerts:v1:${tenantId}:${userId}`
}

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string')

/** localStorage değerini doğrular; bozuk/eski biçimde null (ilk açılış gibi davranılır). */
export function parseNotifyState(raw: string | null): NotifyState | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<NotifyState>
    if (!isStringArray(v.seen) || !isStringArray(v.alerted) || !Array.isArray(v.items) || typeof v.fetchedAt !== 'number') return null
    const items = v.items.filter(
      (i): i is CargoProblemAlertItem => !!i && typeof i.orderId === 'string' && typeof i.orderNumber === 'string' && typeof i.fullName === 'string' && typeof i.label === 'string',
    )
    return { seen: v.seen, alerted: v.alerted, items, fetchedAt: v.fetchedAt }
  } catch {
    return null
  }
}

export function shouldFetch(state: NotifyState | null, now: number): boolean {
  return !state || now - state.fetchedAt >= NOTIFY_SHARED_FETCH_GAP_MS
}

/**
 * Yeni çekilen listeyi duruma işler. `toAlert`: bu çekimde ilk kez görülen yeni siparişler (sesli uyarı için).
 * Önceki durum yoksa liste olduğu gibi görülmüş sayılır.
 */
export function applyFetch(prev: NotifyState | null, items: CargoProblemAlertItem[], now: number): { state: NotifyState; toAlert: string[] } {
  const ids = items.map((i) => i.orderId)
  if (!prev) return { state: { seen: ids, alerted: ids, items, fetchedAt: now }, toAlert: [] }
  const current = new Set(ids)
  const seen = prev.seen.filter((id) => current.has(id))
  const alerted = prev.alerted.filter((id) => current.has(id))
  const seenSet = new Set(seen)
  const alertedSet = new Set(alerted)
  const toAlert = ids.filter((id) => !seenSet.has(id) && !alertedSet.has(id))
  return { state: { seen, alerted: [...alerted, ...toAlert], items, fetchedAt: now }, toAlert }
}

/** Henüz görülmemiş (yeni) öğeler, sunucu sırasıyla. */
export function unseenItems(state: NotifyState | null): CargoProblemAlertItem[] {
  if (!state) return []
  const seen = new Set(state.seen)
  return state.items.filter((i) => !seen.has(i.orderId))
}

/** Verilen siparişleri (verilmezse listedekilerin tümünü) görüldü işaretler. */
export function markSeen(state: NotifyState, orderIds?: string[]): NotifyState {
  const add = orderIds ?? state.items.map((i) => i.orderId)
  const seen = new Set(state.seen)
  for (const id of add) seen.add(id)
  const alerted = new Set(state.alerted)
  for (const id of add) alerted.add(id)
  return { ...state, seen: [...seen], alerted: [...alerted] }
}

/** Sesli uyarı tercihi (tarayıcı başına); kayıt yoksa açık. */
export function parseSoundPref(raw: string | null): boolean {
  return raw !== '0'
}

/** Sorunlu Kargolar'da ilgili siparişin iletişim çekmecesini açan bağlantı. */
export function cargoProblemOrderHref(orderId: string): string {
  return `/admin/sorunlu-kargolar?tab=mudahale&order=${encodeURIComponent(orderId)}`
}
